import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faEnvelope, faUserCheck } from '@/icons';
import { clearSignupSession, useSignupSession } from '../contexts/SignupSessionContext';

export const RegisterTeamCompletePage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { session } = useSignupSession();

    useEffect(() => {
        document.title = 'Team sign up complete | Calculist';
        clearSignupSession();
    }, []);

    const recipients: { name: string; email: string }[] = [];
    if (session.email) {
        const name = [session.firstName, session.lastName].filter(Boolean).join(' ') || session.email;
        recipients.push({ name, email: session.email });
    }
    session.members.forEach(m => {
        const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;
        recipients.push({ name, email: m.email });
    });

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
            paddingTop: theme.spacing.xl,
        }}>
            <div style={{ ...themedStyles.card.default, maxWidth: '560px', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.lg }} />
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: 0 }}>Sign Up Progress</h1>
                </div>

                {/* Step 1 — Team Sign Up: complete */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.md,
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: theme.borderRadius.lg,
                    padding: theme.spacing.md,
                    marginBottom: theme.spacing.md,
                }}>
                    <div style={{ color: '#16a34a', fontSize: '24px', flexShrink: 0, marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <div>
                        <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: '#16a34a', marginBottom: '2px' }}>
                            Step 1 of 2 — Team Sign Up
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.xs }}>
                            Complete
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                            Your team has been created and verification emails have been sent to all members.
                        </div>
                    </div>
                </div>

                {/* Step 2 — User Sign Up: pending for each member */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.md,
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: theme.borderRadius.lg,
                    padding: theme.spacing.md,
                    marginBottom: theme.spacing.xl,
                }}>
                    <div style={{ color: '#2563eb', fontSize: '24px', flexShrink: 0, marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faUserCheck} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: '#2563eb', marginBottom: '2px' }}>
                            Step 2 of 2 — User Sign Up
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.xs }}>
                            Action required — each team member must complete this step
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.md }}>
                            Every team member (including you) will receive a verification email. Each person must click the link and complete their individual sign up to access Calculist.
                        </div>

                        {recipients.length > 0 && (
                            <div style={{
                                backgroundColor: theme.colors.background,
                                border: `1px solid #bfdbfe`,
                                borderRadius: theme.borderRadius.md,
                                padding: theme.spacing.sm,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing.xs,
                            }}>
                                <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.secondary, marginBottom: '2px' }}>
                                    Verification emails sent to
                                </div>
                                {recipients.map((r, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                                            <FontAwesomeIcon icon={faEnvelope} style={{ color: '#2563eb', fontSize: '13px', flexShrink: 0 }} />
                                            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.primary }}>
                                                {r.name !== r.email ? `${r.name} — ${r.email}` : r.email}
                                            </span>
                                        </div>
                                        <span style={{
                                            fontSize: theme.typography.fontSize.xs,
                                            fontWeight: theme.typography.fontWeight.semibold,
                                            color: '#2563eb',
                                            backgroundColor: '#eff6ff',
                                            padding: '2px 8px',
                                            borderRadius: '999px',
                                            border: '1px solid #bfdbfe',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            Email sent
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{
                    backgroundColor: '#fffbeb',
                    border: '2px solid #f59e0b',
                    borderRadius: theme.borderRadius.lg,
                    padding: theme.spacing.lg,
                    marginBottom: theme.spacing.lg,
                    textAlign: 'center',
                }}>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: '#92400e' }}>
                        Check your email{session.email ? <> ({session.email})</> : ''} and follow the instructions in it to finalize your sign up process.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => navigate('/auth/login')}
                    style={{
                        ...themedStyles.button.primary,
                        width: '100%',
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                        textAlign: 'center',
                    }}
                >
                    Go to sign in
                </button>
            </div>
        </div>
    );
};
